import { MathJaxContext } from 'better-react-mathjax';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { useState, useEffect, createContext, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useMatch, useNavigate } from 'react-router-dom';

import { getUserFromDb, startNewSession } from './Common/Database';
import { auththentication } from './Common/Firebase';
import Chatbot from './Components/Chatbot';
import ConnectionModal from './Components/ConnectionModal';
import Header from './Components/Header';
import Loader from './Components/Loader';
import { cleanCount } from './redux/slices/backHistorySlice';
import { setSubjectsStudent, setStudentNames } from './redux/slices/nameStudentsSlice';
import { setAuthUser } from './redux/slices/userSlice';
import AppRoutes from './services/routes';

export const AuthContext = createContext({
  user: null,
});

function App() {
  const dispatch = useDispatch();

  const [user, setUser] = useState(null);
  const userType = user?.type;
  const userActive = user?.active === false ? 'false' : null;
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [authAttempt, setAuthAttempt] = useState(0);
  const authRequest = useRef(0);
  const [showModal, setShowModal] = useState(false);

  const navigate = useNavigate();
  const isPublicMaterial = Boolean(useMatch('/publicMaterial/:id'));
  const navigateRef = useRef(navigate);
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);
  const { t } = useTranslation();

  const isStudent = user?.type === 'Student';

  useEffect(() => {
    const originalConsoleError = console.error;
    console.error = function () {
      const errorMessage = Array.from(arguments).join(' ');
      if (errorMessage.includes("Could not reach Cloud Firestore backend. Backend didn't respond within 10 seconds.")) {
        setShowModal(true);
      }
      originalConsoleError.apply(console, arguments);
    };
    return () => {
      console.error = originalConsoleError;
    };
  }, []);

  useEffect(() => {
    let timer;
    if (showModal) {
      timer = setTimeout(() => {
        setShowModal(false);
      }, 5000);
    }

    return () => clearTimeout(timer);
  }, [showModal]);

  useEffect(() => {
    if (isPublicMaterial) return;
    const getCurrentTimeInKyiv = () => {
      return new Date(
        new Intl.DateTimeFormat('en-US', {
          timeZone: 'Europe/Kiev',
        }).format(new Date()),
      );
    };

    const shouldStartNewSession = () => {
      const lastSessionTimestamp = localStorage.getItem('lastSessionTimestamp');
      const currentTime = getCurrentTimeInKyiv().getTime();

      if (!lastSessionTimestamp || currentTime - parseInt(lastSessionTimestamp) >= 60 * 60 * 1000) {
        return true;
      }

      return false;
    };

    if (user && ['Teacher', 'Student'].includes(user.type) && user.email && shouldStartNewSession()) {
      localStorage.setItem('lastSessionTimestamp', getCurrentTimeInKyiv().getTime().toString());
      startNewSession(user);
    }

    const interval = setInterval(
      () => {
        if (user && (user.type === 'Teacher' || user.type === 'Student')) {
          if (shouldStartNewSession()) {
            startNewSession(user);
          }
        }
      },
      60 * 60 * 1000,
    );

    return () => {
      clearInterval(interval);
    };
  }, [user, isPublicMaterial]);

  const clearAuthState = useCallback(() => {
    setUser(null);
    dispatch(setAuthUser(null));
    dispatch(setSubjectsStudent({}));
    dispatch(setStudentNames(''));
    dispatch(cleanCount());
    localStorage.removeItem('user');
    localStorage.removeItem('userActive');
    localStorage.removeItem('lastSessionTimestamp');
    sessionStorage.setItem('history', JSON.stringify({ count: 0, prevPageArray: ['home'] }));
  }, [dispatch]);

  useEffect(() => {
    // Public links never depend on a Firebase user or role profile. Keep private
    // routes gated until Auth is resolved again after leaving the public viewer.
    if (isPublicMaterial) {
      ++authRequest.current;
      setAuthLoading(true);
      return;
    }
    let disposed = false;
    const unsubscribe = onAuthStateChanged(
      auththentication,
      async current => {
        if (disposed) return;
        const request = ++authRequest.current;
        clearAuthState();
        setFirebaseUser(current);
        setAuthError(null);
        setAuthLoading(Boolean(current));
        if (!current) {
          navigateRef.current('/');
          return;
        }
        try {
          const profile = await getUserFromDb(current);
          if (disposed || request !== authRequest.current || auththentication.currentUser?.uid !== current.uid) return;
          localStorage.setItem('user', JSON.stringify(profile));
          if (typeof profile.active === 'boolean') localStorage.setItem('userActive', String(profile.active));
          dispatch(setSubjectsStudent(profile.type === 'Student' ? profile.subjects || {} : {}));
          dispatch(setAuthUser(profile));
          setUser(profile);
          if (profile.active === false) navigateRef.current('/');
        } catch (error) {
          if (disposed || request !== authRequest.current) return;
          setAuthError(
            error.code === 'auth/profile-conflict'
              ? 'Знайдено конфлікт профілів користувача. Зверніться до адміністратора.'
              : error.code === 'auth/unsupported-role'
                ? 'Не вдалося визначити підтримувану роль. Зверніться до адміністратора.'
                : 'Не вдалося завантажити профіль. Спробуйте ще раз або зверніться до адміністратора.',
          );
        } finally {
          if (!disposed && request === authRequest.current) setAuthLoading(false);
        }
      },
      () => {
        if (disposed) return;
        ++authRequest.current;
        clearAuthState();
        setAuthLoading(false);
        setAuthError('Не вдалося відновити авторизацію. Спробуйте ще раз.');
      },
    );
    return () => {
      disposed = true;
      // This counter invalidates requests; it is not a DOM ref.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      ++authRequest.current;
      unsubscribe();
    };
  }, [authAttempt, clearAuthState, dispatch, isPublicMaterial]);

  const logout = async () => {
    ++authRequest.current;
    clearAuthState();
    setAuthLoading(true);
    try {
      await signOut(auththentication);
    } catch (error) {
      setAuthError('Не вдалося вийти з акаунта. Спробуйте ще раз.');
    } finally {
      setAuthLoading(false);
    }
  };

  const memoizedValue = useMemo(() => ({ user }), [user]);

  return (
    <MathJaxContext config={{ loader: { load: ['input/asciimath'] } }}>
      <AuthContext.Provider value={memoizedValue}>
        <div>
          <Header
            user={user}
            userType={userType}
            userMail={user?.mail}
            authLoading={isPublicMaterial ? false : authLoading}
            isAuthenticated={Boolean(firebaseUser)}
            onLogout={logout}
          />
          <Chatbot />
          <main>
            <section className="layout_section">
              {isPublicMaterial ? (
                <AppRoutes user={user} userType={userType} />
              ) : authLoading ? (
                <Loader />
              ) : authError ? (
                <div role="alert">
                  <p>{authError}</p>
                  <button onClick={() => setAuthAttempt(value => value + 1)}>Спробувати ще раз</button>
                </div>
              ) : (
                <AppRoutes user={user} userType={userType} userActive={userActive} />
              )}
            </section>

            {!isStudent ? (
              <>
                <div className="decor-header"></div>
                <div className="decor-first"></div>
                <div className="decor-sec"></div>
              </>
            ) : (
              <>
                <div className="student-decor-header-left"></div>
                <div className="student-decor-header-rigth"></div>
                <div className="student-decor-first"></div>
                <div className="student-decor-second"></div>
              </>
            )}
          </main>

          {showModal && (
            <ConnectionModal message={t('Sorry, your internet connection is bad! Please wait, loading.')} />
          )}
        </div>
      </AuthContext.Provider>
    </MathJaxContext>
  );
}

export default App;
